import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Modal,
  Form,
  Badge,
  InputGroup,
  Alert
} from 'react-bootstrap';
import {
  Plus,
  Pencil,
  Trash2,
  Dash,
  Search,
  Filter,
  Laptop,
  Phone
} from 'react-bootstrap-icons';
import { FaGripVertical } from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DataApi from '../../api/DataApi';
import AppToast from '../AppToast';
import authApi from '../../api/authApi';
import './css/TabManager.css';
import { getEntitlementsSection } from '../../utils/entitlements';

const TabManager = () => {
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTab, setEditingTab] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tabToDelete, setTabToDelete] = useState(null);
  const [filters, setFilters] = useState({
    name: '',
    label: '',
    object_name: '',
    type: '',
    is_active: '',
    is_standard: ''
  });
  const [objects, setObjects] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [entitlementsFetched, setEntitlementsFetched] = useState(false);
  const [tabEntitlements, setTabEntitlements] = useState(null);
  const [objectManagerEntitlements, setObjectManagerEntitlements] = useState(null);
  const [showAddStandardModal, setShowAddStandardModal] = useState(false);
  const [inactiveStandardTabs, setInactiveStandardTabs] = useState([]);
  const [selectedTabIdsToAdd, setSelectedTabIdsToAdd] = useState(new Set());
  const [loadingInactiveTabs, setLoadingInactiveTabs] = useState(false);
  const [addingTabs, setAddingTabs] = useState(false);

  // Tab and objectManager from session. Fetch on mount and use API response so allowedObjects / maxTabs are from session.
  useEffect(() => {
    authApi.fetchEntitlements().then((res) => {
      if (res?.entitlements?.tab != null) setTabEntitlements(res.entitlements.tab);
      if (res?.entitlements?.objectManager != null) setObjectManagerEntitlements(res.entitlements.objectManager);
      setEntitlementsFetched(true);
    });
  }, []);

  const tabConfig = tabEntitlements ?? getEntitlementsSection('tab', {});
  const objectManagerConfig = objectManagerEntitlements ?? getEntitlementsSection('objectManager', {});
  const allowTabCreate = tabConfig?.allowCreate !== false;
  const canEditStandardTab = tabConfig?.allowStandardEdit === true;
  const canEditTabLabel = canEditStandardTab || tabConfig?.allowEditLabel === true;
  const canEditTab = (tab) => !tab?.is_standard || canEditTabLabel;
  // Delete shows for all tabs except Home (standard). Home cannot be deleted; other standard tabs → deactivate; custom → permanent delete.
  const isHomeTab = (tab) => (tab?.name || '').toLowerCase() === 'home' || (tab?.label || '').toLowerCase() === 'home';
  const canDeleteTab = (tab) => !isHomeTab(tab);
  const canEditTabProperties = (tab) => !tab?.is_standard || canEditStandardTab;
  const isStandardEditLabelOnly = editingTab?.is_standard && canEditTabLabel && !canEditStandardTab;
  // Match ObjectManager / FieldManager: only when license explicitly enables standard (admin "Enable Standard")
  const enableStandardTab =
    objectManagerConfig?.enable_standard === true ||
    objectManagerConfig?.enableStandard === true;
  const maxTabs = Number(tabConfig?.maxTabs);
  const customTabCount = tabs.filter(tab => !tab?.is_standard).length;
  const isTabLimitReached = Number.isFinite(maxTabs)
    && maxTabs >= 0
    && customTabCount >= maxTabs;
  const canCreateTab = entitlementsFetched && allowTabCreate && !isTabLimitReached;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    label: '',
    object_name: '',
    is_active: true,
    order_no: 0,
    is_standard: false,
    type: 'object',
    url: '',
    display_on: 'web,mobile',
    group_key: '',
    group_label: '',
    group_selection: '' // '' = no group, group_key = existing group, '__new__' = show key/label inputs
  });

  useEffect(() => {
    loadTabs();
  }, [filters]);

  useEffect(() => {
    loadObjects();
  }, []);

  const loadTabs = async () => {
    try {
      setLoading(true);
      const activeFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '')
      );
      console.log("Active filters:", activeFilters);

      // Build query string from filters
      const queryParams = new URLSearchParams();
      Object.entries(activeFilters).forEach(([key, value]) => {
        queryParams.append(key, value);
      });

      const queryString = queryParams.toString();
      console.log("Query string:", queryString);
      const tabApi = new DataApi('tabs');
      const response = await tabApi.fetchAll(queryString);
      console.log("response: ", response);
      if (response.data.success) {
        setTabs(response.data.data || []);
      }
    } catch (error) {
      showToast('Error loading tabs: ' + error.message, 'danger');
    } finally {
      setLoading(false);
    }
  };

  const loadObjects = async () => {
    try {
      const objectApi = new DataApi('objectconfig');
      const response = await objectApi.fetchAll();
      if (response.data.success) {
        // Object name dropdown: only custom objects. Standard objects get tabs from the system.
        const customOnly = (response.data.data || []).filter(obj => !obj?.is_standard);
        setObjects(customOnly);
      }
    } catch (error) {
      console.error('Error loading objects:', error);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const handleCreate = () => {
    if (!allowTabCreate) {
      showToast('Tab creation is disabled for this tenant.', 'danger');
      return;
    }
    if (isTabLimitReached) {
      showToast(`Tab limit reached (${maxTabs}).`, 'danger');
      return;
    }
    setEditingTab(null);
    setFormData({
      name: '',
      label: '',
      object_name: '',
      is_active: true,
      order_no: 0,
      is_standard: false,
      type: 'object',
      url: '',
      display_on: 'web,mobile',
      group_key: '',
      group_label: '',
      group_selection: ''
    });
    setShowModal(true);
  };

  const existingGroups = React.useMemo(() => {
    const seen = new Set();
    const list = [];
    tabs.forEach((tab) => {
      const key = (tab.config?.group_key ?? tab.group_key)?.trim();
      if (!key || seen.has(key.toLowerCase())) return;
      seen.add(key.toLowerCase());
      const label = (tab.config?.group_label ?? tab.group_label)?.trim() || key.replace(/\b\w/g, (c) => c.toUpperCase());
      list.push({ group_key: key, group_label: label });
    });
    return list.sort((a, b) => (a.group_label || a.group_key).localeCompare(b.group_label || b.group_key));
  }, [tabs]);

  const handleEdit = (tab) => {
    setEditingTab(tab);
    const gk = tab.config?.group_key ?? tab.group_key ?? '';
    const gl = tab.config?.group_label ?? tab.group_label ?? '';
    const inExisting = existingGroups.some((g) => (g.group_key || '').toLowerCase() === (gk || '').toLowerCase());
    setFormData({
      name: tab.name,
      label: tab.label,
      object_name: tab.object_name || '',
      is_active: tab.is_active,
      order_no: tab.order_no,
      is_standard: enableStandardTab ? tab.is_standard : false,
      type: tab.type || 'object',
      url: tab.url || '',
      display_on: tab.display_on || 'web,mobile',
      group_key: gk,
      group_label: gl,
      group_selection: gk ? (inExisting ? gk : '__new__') : ''
    });
    setShowModal(true);
  };

  const handleDelete = (tab) => {
    setTabToDelete(tab);
    setShowDeleteModal(true);
  };

  const openAddStandardModal = async () => {
    setShowAddStandardModal(true);
    setSelectedTabIdsToAdd(new Set());
    setLoadingInactiveTabs(true);
    try {
      const tabApi = new DataApi('tabs');
      const response = await tabApi.fetchAll('is_deleted=true&is_standard=true');
      if (response.data?.success && Array.isArray(response.data.data)) {
        setInactiveStandardTabs(response.data.data);
      } else {
        setInactiveStandardTabs([]);
      }
    } catch (err) {
      showToast('Error loading hidden standard tabs: ' + (err.message || 'Unknown'), 'danger');
      setInactiveStandardTabs([]);
    } finally {
      setLoadingInactiveTabs(false);
    }
  };

  const toggleTabToAdd = (tabId) => {
    setSelectedTabIdsToAdd(prev => {
      const next = new Set(prev);
      if (next.has(tabId)) next.delete(tabId);
      else next.add(tabId);
      return next;
    });
  };

  const confirmAddStandardTabs = async () => {
    if (selectedTabIdsToAdd.size === 0) {
      showToast('Select at least one tab to add.', 'warning');
      return;
    }
    setAddingTabs(true);
    try {
      const tabApi = new DataApi('tabs');
      for (const id of selectedTabIdsToAdd) {
        await tabApi.update({ is_deleted: false }, id);
      }
      showToast(`Added ${selectedTabIdsToAdd.size} standard tab(s) to the list.`);
      setShowAddStandardModal(false);
      loadTabs();
    } catch (err) {
      showToast('Error adding tabs: ' + (err.response?.data?.message || err.message), 'danger');
    } finally {
      setAddingTabs(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!editingTab && !canCreateTab) {
        showToast(
          allowTabCreate ? `Tab limit reached (${maxTabs}).` : 'Tab creation is disabled.',
          'danger'
        );
        return;
      }
      // Validate form data
      if (isStandardEditLabelOnly) {
        if (!formData.label || !formData.label.trim()) {
          showToast('Label is required', 'danger');
          return;
        }
      } else {
        if (!formData.name || !formData.label) {
          showToast('Name and label are required', 'danger');
          return;
        }
        // Do not allow __x in name when creating custom tab; it is added automatically
        if (formData.type === 'custom' && formData.name && formData.name.includes('__x')) {
          showToast('Tab name must not contain __x; it is added automatically for custom tabs.', 'danger');
          return;
        }
        if (formData.type === 'object' && !formData.object_name) {
          showToast('Object name is required for object type tabs', 'danger');
          return;
        }
        if (!formData.display_on || formData.display_on.trim() === '') {
          showToast('Please select at least one platform (Web or Mobile)', 'danger');
          return;
        }
      }

      // Ensure object_name is null (not empty string) for custom tabs; store group in config
      const config = { ...(formData.config || {}) };
      const gk = formData.group_key?.trim();
      const gl = formData.group_label?.trim();
      if (gk) {
        config.group_key = gk;
        if (gl) config.group_label = gl;
        const existingInGroup = tabs.find((t) => (t.config?.group_key ?? t.group_key)?.toLowerCase() === gk.toLowerCase());
        if (existingInGroup?.config?.group_order != null) config.group_order = existingInGroup.config.group_order;
      }
      let nameToUse = formData.name;
      if (!editingTab && formData.type === 'custom' && nameToUse) {
        nameToUse = (nameToUse || '').trim();
        if (nameToUse && !nameToUse.endsWith('__x')) {
          nameToUse = nameToUse + '__x';
        }
      }
      const submitData = isStandardEditLabelOnly
        ? { label: formData.label }
        : {
          ...formData,
          name: nameToUse ?? formData.name,
          object_name: formData.type === 'custom' ? null : formData.object_name,
          config: Object.keys(config).length ? config : null
        };
      if (!isStandardEditLabelOnly && !enableStandardTab) submitData.is_standard = false;

      const tabApi = new DataApi('tabs');

      if (editingTab) {
        const response = await tabApi.update(submitData, editingTab.id);
        if (response.data.success) {
          showToast('Tab updated successfully');
          setShowModal(false);
          loadTabs();
        }
      } else {
        const response = await tabApi.create(submitData);
        if (response.data.success) {
          showToast('Tab created successfully');
          setShowModal(false);
          loadTabs();
        }
      }
    } catch (error) {
      const serverMsg = error.response?.data?.message;
      const msg = serverMsg || (error.message ? `Error saving tab: ${error.message}` : 'Error saving tab');
      showToast(msg, 'danger');
    }
  };

  const confirmDelete = async () => {
    try {
      const tabApi = new DataApi('tabs');
      const response = await tabApi.delete(`${tabToDelete.id}/permanent`);
      if (response.data.success) {
        showToast('Tab permanently deleted successfully');
        setShowDeleteModal(false);
        loadTabs();
      }
    } catch (error) {
      showToast('Error deleting tab: ' + error.message, 'danger');
    }
  };

  const confirmRemoveStandard = async () => {
    try {
      const tabApi = new DataApi('tabs');
      const response = await tabApi.delete(tabToDelete.id);
      if (response.data?.success) {
        showToast('Standard tab removed from list');
        setShowDeleteModal(false);
        loadTabs();
      } else {
        throw new Error(response.data?.message || 'Delete failed');
      }
    } catch (error) {
      showToast('Error removing tab: ' + (error.response?.data?.message || error.message), 'danger');
    }
  };



  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      name: '',
      label: '',
      object_name: '',
      type: '',
      is_active: '',
      is_standard: ''
    });
  };

  const getTypeBadge = (type) => {
    return type === 'custom' ? (
      <span className="tab-type-badge tab-type-custom">Custom</span>
    ) : (
      <span className="tab-type-badge tab-type-object">Object</span>
    );
  };

  const getStandardBadge = (isStandard) => {
    return isStandard ?
      <Badge bg="info">Standard</Badge> :
      <Badge bg="light" text="dark">Custom</Badge>;
  };

  const getDisplayOnIcons = (displayOn) => {
    if (!displayOn) return <span className="text-muted">—</span>;
    const platforms = displayOn.split(',').map(p => p.trim()).filter(Boolean);
    const hasWeb = platforms.includes('web');
    const hasMobile = platforms.includes('mobile');
    if (!hasWeb && !hasMobile) return <span className="text-muted">—</span>;
    return (
      <span className="tab-platform-icons" title={platforms.join(', ')}>
        {hasWeb && <Laptop className="tab-platform-icon tab-platform-web" size={18} title="Web" />}
        {hasMobile && <Phone className="tab-platform-icon tab-platform-mobile" size={18} title="Mobile" />}
      </span>
    );
  };

  const handleToggleActive = async (tab) => {
    const nextActive = !tab.is_active;
    setTabs(prev => prev.map(t => t.id === tab.id ? { ...t, is_active: nextActive } : t));
    try {
      const tabApi = new DataApi('tabs');
      const response = await tabApi.update({ ...tab, is_active: nextActive }, tab.id);
      if (response.data?.success) {
        showToast(nextActive ? 'Tab enabled' : 'Tab disabled');
      } else {
        throw new Error(response.data?.message || 'Update failed');
      }
    } catch (error) {
      setTabs(prev => prev.map(t => t.id === tab.id ? { ...t, is_active: tab.is_active } : t));
      showToast('Error updating status: ' + (error.response?.data?.message || error.message), 'danger');
    }
  };

  const parseObjectConfig = (rawConfig) => {
    if (!rawConfig) return {};
    if (typeof rawConfig === 'string') {
      try {
        const parsed = JSON.parse(rawConfig);
        return parsed && typeof parsed === 'object' ? parsed : {};
      } catch (error) {
        console.warn('Failed to parse object config:', error);
        return {};
      }
    }
    return typeof rawConfig === 'object' ? rawConfig : {};
  };

  const canCreateObjectTab = (obj) => {
    const config = parseObjectConfig(obj?.config);
    return !(
      config?.allow_tab === false
      || config?.allow_tab === 'false'
      || config?.allow_tab === 0
      || config?.allow_tab === '0'
    );
  };

  const getTabGroupKey = (tab) => (tab.config?.group_key ?? tab.group_key)?.trim() || '';
  const getTabGroupLabel = (tab) => (tab.config?.group_label ?? tab.group_label)?.trim() || '';

  // Bar slots: one entry per top-level tab or per group, in menu bar order (so groups can sit between top-level tabs)
  const barSlots = React.useMemo(() => {
    const sorted = [...tabs].sort((a, b) => (Number(a.order_no) ?? 0) - (Number(b.order_no) ?? 0));
    const slots = [];
    const groupSeen = new Set();
    sorted.forEach((tab) => {
      const gk = getTabGroupKey(tab);
      if (!gk) {
        slots.push({ type: 'tab', tab });
        return;
      }
      if (groupSeen.has(gk)) return;
      groupSeen.add(gk);
      const groupTabs = tabs.filter((t) => getTabGroupKey(t) === gk).sort((a, b) => (Number(a.order_no) ?? 0) - (Number(b.order_no) ?? 0));
      slots.push({
        type: 'group',
        key: gk,
        label: getTabGroupLabel(groupTabs[0]) || gk.replace(/\b\w/g, (c) => c.toUpperCase()),
        tabs: groupTabs
      });
    });
    return slots;
  }, [tabs]);

  const isSlotStandard = (slot) => {
    if (slot.type === 'tab') return slot.tab.is_standard === true;
    if (slot.type === 'group' && slot.tabs?.length) return slot.tabs.every((t) => t.is_standard === true);
    return false;
  };

  const sortedTabs = React.useMemo(
    () => [...tabs].sort((a, b) => (Number(a.order_no) ?? 0) - (Number(b.order_no) ?? 0)),
    [tabs]
  );

  const [viewMode, setViewMode] = useState('grouped'); // 'grouped' | 'flat'

  const handleDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    if (result.source.droppableId !== 'all-tabs') return;
    const list = [...sortedTabs];
    const [removed] = list.splice(result.source.index, 1);
    list.splice(result.destination.index, 0, removed);
    const merged = list.map((tab, index) => ({ ...tab, order_no: index }));
    setTabs(merged);
    try {
      const tabApi = new DataApi('tabs');
      const tabOrders = merged.map((tab) => ({ id: tab.id, order_no: tab.order_no }));
      const response = await tabApi.put('/reorder', { tabOrders });
      if (response.data?.success) showToast('Tab order updated');
      else throw new Error(response.data?.message || 'Reorder failed');
    } catch (error) {
      showToast('Error updating tab order: ' + (error.response?.data?.message || error.message), 'danger');
      loadTabs();
    }
  };

  // Reorder bar slots (single list: each top-level tab or each group)
  const handleBarSlotDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    if (result.source.droppableId !== 'bar-slots-all') return;
    const list = [...barSlots];
    const [removed] = list.splice(result.source.index, 1);
    list.splice(result.destination.index, 0, removed);
    const flatTabs = list.flatMap((slot) => (slot.type === 'tab' ? [slot.tab] : slot.tabs));
    const tabOrders = flatTabs.map((tab, index) => ({ id: tab.id, order_no: index }));
    setTabs((prev) => {
      const byId = new Map(prev.map((t) => [t.id, t]));
      tabOrders.forEach(({ id, order_no }) => {
        const t = byId.get(id);
        if (t) byId.set(id, { ...t, order_no });
      });
      return [...byId.values()].sort((a, b) => (Number(a.order_no) ?? 0) - (Number(b.order_no) ?? 0));
    });
    try {
      const tabApi = new DataApi('tabs');
      const response = await tabApi.put('/reorder', { tabOrders });
      if (response.data?.success) {
        showToast('Menu bar order updated');
        loadTabs();
      } else throw new Error(response.data?.message || 'Reorder failed');
    } catch (error) {
      showToast('Error updating order: ' + (error.response?.data?.message || error.message), 'danger');
      loadTabs();
    }
  };

  // Reorder tabs within a single group (group tab bar section)
  const handleGroupTabsDragEnd = async (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const droppableId = result.source.droppableId;
    if (!droppableId.startsWith('group-tabs-')) return;
    const groupKey = droppableId.replace('group-tabs-', '');
    const slot = barSlots.find((s) => s.type === 'group' && s.key === groupKey);
    if (!slot) return;
    const reorderedTabs = Array.from(slot.tabs);
    const [removed] = reorderedTabs.splice(result.source.index, 1);
    reorderedTabs.splice(result.destination.index, 0, removed);
    const updatedBarSlots = barSlots.map((s) =>
      s.type === 'group' && s.key === groupKey ? { ...s, tabs: reorderedTabs } : s
    );
    const flatTabs = updatedBarSlots.flatMap((s) => (s.type === 'tab' ? [s.tab] : s.tabs));
    const tabOrders = flatTabs.map((tab, index) => ({ id: tab.id, order_no: index }));
    setTabs((prev) => {
      const byId = new Map(prev.map((t) => [t.id, t]));
      tabOrders.forEach(({ id, order_no }) => {
        const t = byId.get(id);
        if (t) byId.set(id, { ...t, order_no });
      });
      return [...byId.values()].sort((a, b) => (Number(a.order_no) ?? 0) - (Number(b.order_no) ?? 0));
    });
    try {
      const tabApi = new DataApi('tabs');
      const response = await tabApi.put('/reorder', { tabOrders });
      if (response.data?.success) {
        showToast('Group tab order updated');
        loadTabs();
      } else throw new Error(response.data?.message || 'Reorder failed');
    } catch (error) {
      showToast('Error updating group tab order: ' + (error.response?.data?.message || error.message), 'danger');
      loadTabs();
    }
  };

  const handleUnifiedDragEnd = async (result) => {
    if (!result.destination) return;
    const src = result.source.droppableId;
    if (src === 'bar-slots-all') {
      await handleBarSlotDragEnd(result);
      return;
    }
    if (src.startsWith('group-tabs-')) {
      await handleGroupTabsDragEnd(result);
    }
  };

  const getAvailableObjects = () => {
    // Get all object names that already have tabs (case-insensitive comparison)
    const existingObjectNames = tabs
      .filter(tab => tab.object_name && tab.type === 'object' && tab.id !== editingTab?.id)
      .map(tab => tab.object_name.toLowerCase());

    console.log("Existing object names in tabs:", existingObjectNames);
    console.log("All objects from object_config:", objects.map(obj => obj.object_name));
    console.log("Editing tab:", editingTab);

    // Filter out objects that already have tabs (but include the current object if editing)
    // Also exclude history objects and attachment as an extra safety measure
    const availableObjects = objects.filter(obj =>
      !existingObjectNames.includes(obj.object_name.toLowerCase()) &&
      canCreateObjectTab(obj)
    );
    console.log("Available objects:", availableObjects.map(obj => obj.object_name));

    return availableObjects;
  };

  return (
    <Container fluid>
      <Row className="mb-3 mt-3">
        <Col>
          <h2>Tab Manager</h2>
          <p className="text-muted">Manage application tabs and navigation</p>
        </Col>
        <Col xs="auto" className="d-flex gap-2">
          {enableStandardTab && (
            <Button
              variant="outline-primary"
              onClick={openAddStandardModal}
              title="Add previously removed standard tabs back to the list"
            >
              <Plus className="me-2" />
              Add standard tab
            </Button>
          )}
          {allowTabCreate && (
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={!canCreateTab}
              title={!canCreateTab
                ? `Tab limit reached (${maxTabs}).`
                : undefined}
            >
              <Plus className="me-2" />
              Create Tab
            </Button>
          )}
        </Col>
      </Row>

      {allowTabCreate && isTabLimitReached && (
        <Alert variant="warning" className="mb-3">
          Tab limit reached ({maxTabs}). You cannot create more tabs until the limit is increased.
        </Alert>
      )}

      {/* Filters */}
      <Card className="mb-3">
        <Card.Body>
          <Row className="align-items-center">
            <Col>
              <InputGroup>
                <InputGroup.Text>
                  <Search />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search tabs..."
                  value={filters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col xs="auto">
              <Button
                variant={showFilters ? "primary" : "outline-secondary"}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="me-2" />
                Filters
              </Button>
            </Col>
            <Col xs="auto">
              <Button variant="outline-secondary" onClick={clearFilters}>
                Clear
              </Button>
            </Col>
          </Row>

          {showFilters && (
            <Row className="mt-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Label</Form.Label>
                  <Form.Control
                    value={filters.label}
                    onChange={(e) => handleFilterChange('label', e.target.value)}
                    placeholder="Filter by label"
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Object Name</Form.Label>
                  <Form.Control
                    value={filters.object_name}
                    onChange={(e) => handleFilterChange('object_name', e.target.value)}
                    placeholder="Filter by object"
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Type</Form.Label>
                  <Form.Select
                    value={filters.type}
                    onChange={(e) => handleFilterChange('type', e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="object">Object</option>
                    <option value="custom">Custom</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group>
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    value={filters.is_active}
                    onChange={(e) => handleFilterChange('is_active', e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              {enableStandardTab && (
                <Col md={2}>
                  <Form.Group>
                    <Form.Label>Standard</Form.Label>
                    <Form.Select
                      value={filters.is_standard}
                      onChange={(e) => handleFilterChange('is_standard', e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="true">Standard</option>
                      <option value="false">Custom</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Tabs Table */}
      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : viewMode === 'grouped' ? (
            <DragDropContext onDragEnd={handleUnifiedDragEnd}>
              {/* Single combined list: all bar slots with Standard/Custom label */}
              {barSlots.length > 0 && (
                <div className="mb-4">
                  <h6 className="text-primary mb-2">Menu bar order</h6>
                  <p className="text-muted small mb-2">
                    Drag rows to set the menu bar order. Each row is labeled Standard or Custom.
                  </p>
                  <Table responsive hover className="tab-table">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }} aria-label="Drag to reorder menu bar" />
                        <th>Tab #</th>
                        <th>Label</th>
                        <th>Name / Group</th>
                        <th>Object</th>
                        <th>Standard / Custom</th>
                        <th>Display On</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <Droppable droppableId="bar-slots-all">
                      {(droppableProvided) => (
                        <tbody ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                          {barSlots.map((slot, index) => (
                            <Draggable
                              key={slot.type === 'tab' ? slot.tab.id : `group-${slot.key}`}
                              draggableId={slot.type === 'tab' ? String(slot.tab.id) : `group-${slot.key}`}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <tr
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={snapshot.isDragging ? 'tab-row-dragging' : ''}
                                >
                                  <td {...provided.dragHandleProps} className="tab-drag-handle" title="Drag to change position in menu bar">
                                    <FaGripVertical className="text-muted" />
                                  </td>
                                  <td>{index + 1}</td>
                                  <td>
                                    {slot.type === 'tab' ? (
                                      slot.tab.label
                                    ) : (
                                      <span className="tab-manager-group-label">
                                        Group Tab
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    {slot.type === 'tab' ? (
                                      <strong>{slot.tab.name}</strong>
                                    ) : (
                                      <strong>{slot.label}</strong>
                                    )}
                                  </td>
                                  <td>{slot.type === 'tab' ? (slot.tab.object_name || '-') : '—'}</td>
                                  <td>{isSlotStandard(slot) ? <Badge bg="info">Standard</Badge> : <Badge bg="light" text="dark">Custom</Badge>}</td>
                                  <td>{slot.type === 'tab' ? getDisplayOnIcons(slot.tab.display_on) : '—'}</td>
                                  <td>
                                    {slot.type === 'tab' ? (
                                      canEditTabProperties(slot.tab) ? (
                                        <Form.Check
                                          type="switch"
                                          id={`active-${slot.tab.id}`}
                                          checked={!!slot.tab.is_active}
                                          onChange={() => handleToggleActive(slot.tab)}
                                          label=""
                                          className="tab-status-toggle"
                                          title={slot.tab.is_active ? 'Active' : 'Inactive'}
                                          aria-label={slot.tab.is_active ? `Disable ${slot.tab.label}` : `Enable ${slot.tab.label}`}
                                        />
                                      ) : (
                                        <span className="text-muted">{slot.tab.is_active ? 'Active' : 'Inactive'}</span>
                                      )
                                    ) : '—'}
                                  </td>
                                  <td>
                                    {slot.type === 'tab' ? (
                                      <div className="action-buttons">
                                        {canEditTab(slot.tab) && <Button variant="outline-primary" size="sm" onClick={() => handleEdit(slot.tab)} title="Edit"><Pencil /></Button>}
                                        {canDeleteTab(slot.tab) && <Button variant="outline-danger" size="sm" onClick={() => handleDelete(slot.tab)} title="Delete"><Trash2 /></Button>}
                                        {!canEditTab(slot.tab) && !canDeleteTab(slot.tab) && <span className="text-muted">—</span>}
                                      </div>
                                    ) : (
                                      <span className="text-muted">—</span>
                                    )}
                                  </td>
                                </tr>
                              )}
                            </Draggable>
                          ))}
                          {droppableProvided.placeholder}
                        </tbody>
                      )}
                    </Droppable>
                  </Table>
                </div>
              )}

              {/* Section 3: Group tab bar — reorder tabs within each group */}
              {barSlots.some((s) => s.type === 'group') && (
                <div className="group-tab-bar-section">
                  <h6 className="text-muted mb-2">Tabs within groups</h6>
                  <p className="text-muted small mb-3">
                    Reorder tabs inside each group. This only changes the order within the group dropdown.
                  </p>
                  {barSlots
                    .filter((s) => s.type === 'group')
                    .map((slot) => (
                      <Card key={`group-bar-${slot.key}`} className="mb-3 group-tab-bar-card">
                        <Card.Header className="group-tab-bar-card-header py-2">
                          <strong>{slot.label}</strong>
                          <span className="text-muted small ms-2">({slot.tabs.length} tabs)</span>
                        </Card.Header>
                        <Card.Body className="py-2">
                          <Table responsive hover className="tab-table">
                            <thead>
                              <tr>
                                <th style={{ width: 36 }} aria-label="Drag to reorder" />
                                <th>Tab #</th>
                                <th>Label</th>
                                <th>Name / Group</th>
                                <th>Object</th>
                                <th>Standard / Custom</th>
                                <th>Display On</th>
                                <th>Status</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <Droppable droppableId={`group-tabs-${slot.key}`}>
                              {(droppableProvided) => (
                                <tbody ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                                  {slot.tabs.map((tab, index) => (
                                    <Draggable
                                      key={tab.id}
                                      draggableId={String(tab.id)}
                                      index={index}
                                    >
                                      {(provided, snapshot) => (
                                        <tr
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          className={snapshot.isDragging ? 'tab-row-dragging' : ''}
                                        >
                                          <td {...provided.dragHandleProps} className="tab-drag-handle" title="Drag to reorder">
                                            <FaGripVertical className="text-muted" />
                                          </td>
                                          <td>{index + 1}</td>
                                          <td>{tab.label}</td>
                                          <td><strong>{tab.name}</strong></td>
                                          <td>{tab.object_name || '-'}</td>
                                          <td>{getStandardBadge(tab.is_standard)}</td>
                                          <td>{getDisplayOnIcons(tab.display_on)}</td>
                                          <td>
                                            {canEditTabProperties(tab) ? (
                                              <Form.Check
                                                type="switch"
                                                id={`active-group-${tab.id}`}
                                                checked={!!tab.is_active}
                                                onChange={() => handleToggleActive(tab)}
                                                label=""
                                                className="tab-status-toggle"
                                                title={tab.is_active ? 'Active' : 'Inactive'}
                                                aria-label={tab.is_active ? `Disable ${tab.label}` : `Enable ${tab.label}`}
                                              />
                                            ) : (
                                              <span className="text-muted">{tab.is_active ? 'Active' : 'Inactive'}</span>
                                            )}
                                          </td>
                                          <td>
                                            <div className="action-buttons">
                                              {canEditTab(tab) && <Button variant="outline-primary" size="sm" onClick={() => handleEdit(tab)} title="Edit"><Pencil /></Button>}
                                              {canDeleteTab(tab) && <Button variant="outline-danger" size="sm" onClick={() => handleDelete(tab)} title="Delete"><Trash2 /></Button>}
                                              {!canEditTab(tab) && !canDeleteTab(tab) && <span className="text-muted">—</span>}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </Draggable>
                                  ))}
                                  {droppableProvided.placeholder}
                                </tbody>
                              )}
                            </Droppable>
                          </Table>
                        </Card.Body>
                      </Card>
                    ))}
                </div>
              )}
            </DragDropContext>
          ) : (
            <DragDropContext onDragEnd={handleDragEnd}>
              <div className="tab-sections">
                <div className="tab-section">
                  <h6 className="text-primary mb-2">All tabs</h6>
                  <p className="text-muted small mb-2">
                    Drag rows to reorder. Each tab is labeled Standard or Custom.
                  </p>
                  <Table responsive hover className="tab-table">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }} aria-label="Drag to reorder" />
                        <th>Order</th>
                        <th>Standard / Custom</th>
                        <th>Name</th>
                        <th>Label</th>
                        <th>Object</th>
                        <th>Type</th>
                        <th>Display On</th>
                        <th>URL</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <Droppable droppableId="all-tabs">
                      {(droppableProvided) => (
                        <tbody ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
                          {sortedTabs.map((tab, index) => (
                            <Draggable key={tab.id} draggableId={String(tab.id)} index={index}>
                              {(draggableProvided, snapshot) => (
                                <tr
                                  ref={draggableProvided.innerRef}
                                  {...draggableProvided.draggableProps}
                                  className={snapshot.isDragging ? 'tab-row-dragging' : ''}
                                >
                                  <td {...draggableProvided.dragHandleProps} className="tab-drag-handle" title="Drag to reorder">
                                    <FaGripVertical className="text-muted" />
                                  </td>
                                  <td>{index + 1}</td>
                                  <td>{getStandardBadge(tab.is_standard)}</td>
                                  <td><strong>{tab.name}</strong></td>
                                  <td>{tab.label}</td>
                                  <td>{tab.object_name || '-'}</td>
                                  <td>{getTypeBadge(tab.type)}</td>
                                  <td>{getDisplayOnIcons(tab.display_on)}</td>
                                  <td><code className="small">{tab.url || '-'}</code></td>
                                  <td>
                                    {canEditTabProperties(tab) ? (
                                      <Form.Check
                                        type="switch"
                                        id={`active-${tab.id}`}
                                        checked={!!tab.is_active}
                                        onChange={() => handleToggleActive(tab)}
                                        label=""
                                        className="tab-status-toggle"
                                        title={tab.is_active ? 'Active' : 'Inactive'}
                                        aria-label={tab.is_active ? `Disable ${tab.label}` : `Enable ${tab.label}`}
                                      />
                                    ) : (
                                      <span className="text-muted">{tab.is_active ? 'Active' : 'Inactive'}</span>
                                    )}
                                  </td>
                                  <td>
                                    <div className="action-buttons">
                                      {canEditTab(tab) && <Button variant="outline-primary" size="sm" onClick={() => handleEdit(tab)} title="Edit"><Pencil /></Button>}
                                      {canDeleteTab(tab) && <Button variant="outline-danger" size="sm" onClick={() => handleDelete(tab)} title="Delete"><Trash2 /></Button>}
                                      {!canEditTab(tab) && !canDeleteTab(tab) && <span className="text-muted">—</span>}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Draggable>
                          ))}
                          {droppableProvided.placeholder}
                        </tbody>
                      )}
                    </Droppable>
                  </Table>
                </div>
              </div>
            </DragDropContext>
          )}

          {!loading && tabs.length > 0 && (
            <div className="d-flex justify-content-end mt-2">
              <Button
                variant={viewMode === 'grouped' ? 'primary' : 'outline-secondary'}
                size="sm"
                className="me-2"
                onClick={() => setViewMode('grouped')}
              >
                By group
              </Button>
              <Button
                variant={viewMode === 'flat' ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setViewMode('flat')}
              >
                Flat list
              </Button>
            </div>
          )}

          {!loading && tabs.length === 0 && (
            <div className="text-center py-4">
              <p className="text-muted">No tabs found</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingTab ? 'Edit Tab' : 'Create New Tab'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isStandardEditLabelOnly ? (
            <Form.Group className="mb-3">
              <Form.Label>Label *</Form.Label>
              <Form.Control
                type="text"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                placeholder="Enter display label"
              />
              <Form.Text className="text-muted">
                You can only edit the display label for this standard tab.
              </Form.Text>
            </Form.Group>
          ) : (
            <>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Type</Form.Label>
                    <Form.Select
                      value={formData.type}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setFormData({
                          ...formData,
                          type: newType,
                          // Clear object_name if switching to custom type (set to null, not empty string)
                          object_name: newType === 'custom' ? null : formData.object_name,
                          // Auto-generate URL based on type
                          url: newType === 'custom' ? '' :
                            (formData.object_name ? `/listview/${formData.object_name.toLowerCase()}` : '')
                        });
                      }}
                    >
                      <option value="object">Object</option>
                      <option value="custom">Custom</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Object Name
                      {formData.type === 'object' && (
                        <span className="text-muted ms-2">
                          ({getAvailableObjects().length} available)
                        </span>
                      )}
                      {editingTab?.type === 'object' && (
                        <span className="text-muted ms-2">
                          (Read-only)
                        </span>
                      )}
                    </Form.Label>
                    <Form.Select
                      value={formData.object_name}
                      onChange={(e) => {
                        const objectName = e.target.value;
                        const selectedObject = getAvailableObjects().find(obj => obj.object_name === objectName);
                        setFormData({
                          ...formData,
                          object_name: objectName,
                          // Auto-populate name and label for object type
                          name: formData.type === 'object' && selectedObject ? selectedObject.object_name : formData.name,
                          label: formData.type === 'object' && selectedObject ? (selectedObject.plural_label || selectedObject.object_label || selectedObject.object_name) : formData.label,
                          // Auto-generate URL for object type
                          url: formData.type === 'object' && objectName ? `/listview/${objectName.toLowerCase()}` : formData.url
                        });
                      }}
                      disabled={formData.type !== 'object' || editingTab?.type === 'object'}
                    >
                      <option value="">Select an object</option>
                      {getAvailableObjects().map((obj) => (
                        <option key={obj.id} value={obj.object_name}>
                          {obj.object_label || obj.object_name}
                        </option>
                      ))}
                    </Form.Select>
                    {formData.type === 'object' && (
                      <Form.Text className="text-muted">
                        Only custom objects are listed; standard objects get tabs from the system.
                      </Form.Text>
                    )}
                    {formData.type === 'object' && getAvailableObjects().length === 0 && !editingTab && (
                      <Form.Text className="text-warning">
                        No custom objects available, or they already have tabs. Create a custom object first or create a custom tab instead.
                      </Form.Text>
                    )}
                    {editingTab?.type === 'object' && (
                      <Form.Text className="text-info">
                        Object name cannot be changed for existing object tabs. You can modify the label and name instead.
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      Label *
                      {formData.type === 'object' && (
                        <span className="text-muted ms-2">(Auto-filled from object's plural label)</span>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.label}
                      onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                      placeholder="Enter display label"
                      readOnly={formData.type === 'object'}
                      className={formData.type === 'object' ? 'bg-light' : ''}
                    />
                    {formData.type === 'object' && (
                      <Form.Text className="text-muted">
                        Label is automatically set to the object's plural label and cannot be changed.
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Name *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter tab name"
                    />
                    {!editingTab && formData.type === 'custom' && (
                      <Form.Text className="text-muted">
                        Lowercase, letters, numbers, underscore. Do not include __x; it is added automatically for custom tabs.
                      </Form.Text>
                    )}
                    {!editingTab && formData.type === 'custom' && formData.name && formData.name.includes('__x') && (
                      <Form.Text className="text-danger">
                        Tab name must not contain __x; it is added automatically for custom tabs.
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Order</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.order_no}
                      onChange={(e) => setFormData({ ...formData, order_no: parseInt(e.target.value) || 0 })}
                      placeholder="Display order"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      URL
                      {formData.type === 'object' && (
                        <span className="text-muted ms-2">(Auto-generated)</span>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder={formData.type === 'object' ? 'Auto-generated from object name' : 'Enter URL path'}
                      readOnly={formData.type === 'object'}
                      className={formData.type === 'object' ? 'bg-light' : ''}
                    />
                    {formData.type === 'object' && (
                      <Form.Text className="text-muted">
                        URL is automatically generated as /listview/{formData.object_name ? formData.object_name.toLowerCase() : 'objectname'}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                  </Form.Group>
                </Col>
                {enableStandardTab && (
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        label="Standard Tab"
                        checked={formData.is_standard}
                        onChange={(e) => setFormData({ ...formData, is_standard: e.target.checked })}
                      />
                    </Form.Group>
                  </Col>
                )}
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Menu group</Form.Label>
                    <Form.Select
                      value={formData.group_selection}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === '') {
                          setFormData({ ...formData, group_selection: '', group_key: '', group_label: '' });
                        } else if (v === '__new__') {
                          setFormData({ ...formData, group_selection: '__new__' });
                        } else {
                          const g = existingGroups.find((x) => (x.group_key || '').toLowerCase() === (v || '').toLowerCase());
                          setFormData({
                            ...formData,
                            group_selection: v,
                            group_key: g?.group_key ?? v,
                            group_label: g?.group_label ?? v
                          });
                        }
                      }}
                    >
                      <option value="">No group (top-level tab)</option>
                      {existingGroups.map((g) => (
                        <option key={(g.group_key || '').toLowerCase()} value={g.group_key}>
                          {g.group_label || g.group_key}
                        </option>
                      ))}
                      <option value="__new__">+ Create new group</option>
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Put this tab in a dropdown with others, or leave as top-level.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              {formData.group_selection === '__new__' && (
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>New group key</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.group_key}
                        onChange={(e) => setFormData({ ...formData, group_key: e.target.value })}
                        placeholder="e.g. watconnect"
                      />
                      <Form.Text className="text-muted">
                        Same key on multiple tabs = one dropdown.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Group dropdown label</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.group_label}
                        onChange={(e) => setFormData({ ...formData, group_label: e.target.value })}
                        placeholder="e.g. WatConnect"
                      />
                      <Form.Text className="text-muted">
                        Text on the dropdown button in the menu.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              )}

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Display On *</Form.Label>
                    <div>
                      <Form.Check
                        inline
                        type="checkbox"
                        id="display-web"
                        label="Web"
                        checked={formData.display_on?.includes('web')}
                        onChange={(e) => {
                          const currentValues = formData.display_on ? formData.display_on.split(',') : [];
                          let newValues;
                          if (e.target.checked) {
                            // Add 'web' if not already present
                            newValues = [...new Set([...currentValues, 'web'])];
                          } else {
                            // Remove 'web'
                            newValues = currentValues.filter(v => v !== 'web');
                          }
                          setFormData({ ...formData, display_on: newValues.join(',') });
                        }}
                      />
                      <Form.Check
                        inline
                        type="checkbox"
                        id="display-mobile"
                        label="Mobile"
                        checked={formData.display_on?.includes('mobile')}
                        onChange={(e) => {
                          const currentValues = formData.display_on ? formData.display_on.split(',') : [];
                          let newValues;
                          if (e.target.checked) {
                            // Add 'mobile' if not already present
                            newValues = [...new Set([...currentValues, 'mobile'])];
                          } else {
                            // Remove 'mobile'
                            newValues = currentValues.filter(v => v !== 'mobile');
                          }
                          setFormData({ ...formData, display_on: newValues.join(',') });
                        }}
                      />
                    </div>
                    <Form.Text className="text-muted">
                      Select where this tab should be displayed
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            {editingTab ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal — standard tab: deactivate and hide; custom tab: permanent delete */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {tabToDelete?.is_standard ? (
            <>
              <p>Delete the standard tab "<strong>{tabToDelete?.label}</strong>"?</p>
              <p className="text-muted mb-0">
                It will be removed from the tab list and menu (soft delete). You can add it again later from &quot;Add standard tab&quot;.
              </p>
              <p className="text-muted mt-3 mb-0 small">
                <strong>Tab:</strong> {tabToDelete?.name} · {tabToDelete?.object_name || tabToDelete?.type || 'N/A'}
              </p>
            </>
          ) : (
            <>
              <p>Are you absolutely sure you want to <strong>permanently delete</strong> the tab "<strong>{tabToDelete?.label}</strong>"?</p>
              <Alert variant="danger">
                <strong>⚠️ CRITICAL WARNING:</strong> This action will permanently remove the tab from the database and cannot be undone. All associated data will be lost forever.
              </Alert>
              <p className="text-muted mt-3">
                <strong>Tab Details:</strong><br />
                Name: {tabToDelete?.name}<br />
                Label: {tabToDelete?.label}<br />
                Object: {tabToDelete?.object_name || 'N/A'}<br />
                Type: {tabToDelete?.type}
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={tabToDelete?.is_standard ? confirmRemoveStandard : confirmDelete}>
            <Trash2 className="me-2" />
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add standard tab modal — left: available tabs, right: selected to add */}
      <Modal show={showAddStandardModal} onHide={() => setShowAddStandardModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Add standard tab</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            Move tabs from the left to the right to select them. They will be reactivated and shown in the tab list and menu.
          </p>
          {loadingInactiveTabs ? (
            <div className="text-center py-4 text-muted">Loading removed standard tabs...</div>
          ) : inactiveStandardTabs.length === 0 ? (
            <div className="text-muted py-3">No removed standard tabs. Remove a standard tab first to see it here.</div>
          ) : (
            <Row>
              <Col md={5}>
                <h6 className="text-muted mb-2">Available</h6>
                <div className="border rounded p-2" style={{ minHeight: 200, maxHeight: 320, overflowY: 'auto' }}>
                  {inactiveStandardTabs
                    .filter((tab) => !selectedTabIdsToAdd.has(tab.id))
                    .map((tab) => (
                      <div
                        key={tab.id}
                        className="d-flex align-items-center justify-content-between py-2 px-2 border-bottom border-light"
                      >
                        <span><strong>{tab.label}</strong>{tab.object_name ? ` · ${tab.object_name}` : ''}</span>
                        <Button variant="outline-primary" size="sm" onClick={() => toggleTabToAdd(tab.id)} title="Add">
                          <Plus />
                        </Button>
                      </div>
                    ))}
                  {inactiveStandardTabs.filter((t) => !selectedTabIdsToAdd.has(t.id)).length === 0 && (
                    <div className="text-muted small py-3 text-center">All selected</div>
                  )}
                </div>
              </Col>
              <Col md={2} className="d-flex align-items-center justify-content-center">
                <span className="text-muted small">→</span>
              </Col>
              <Col md={5}>
                <h6 className="text-muted mb-2">Selected to add</h6>
                <div className="border rounded p-2" style={{ minHeight: 200, maxHeight: 320, overflowY: 'auto' }}>
                  {inactiveStandardTabs
                    .filter((tab) => selectedTabIdsToAdd.has(tab.id))
                    .map((tab) => (
                      <div
                        key={tab.id}
                        className="d-flex align-items-center justify-content-between py-2 px-2 border-bottom border-light"
                      >
                        <span><strong>{tab.label}</strong>{tab.object_name ? ` · ${tab.object_name}` : ''}</span>
                        <Button variant="outline-secondary" size="sm" onClick={() => toggleTabToAdd(tab.id)} title="Remove from selection">
                          <Dash />
                        </Button>
                      </div>
                    ))}
                  {selectedTabIdsToAdd.size === 0 && (
                    <div className="text-muted small py-3 text-center">None selected</div>
                  )}
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddStandardModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={confirmAddStandardTabs}
            disabled={loadingInactiveTabs || addingTabs || inactiveStandardTabs.length === 0 || selectedTabIdsToAdd.size === 0}
          >
            {addingTabs ? 'Adding...' : `Add ${selectedTabIdsToAdd.size} tab(s) to list`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Toast */}
      <AppToast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
      />
    </Container>
  );
};

export default TabManager;
